import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Alert
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

const VendorDetailModal = ({ vendor, patternType, open, onClose }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);

  if (!vendor) return null;

  // Get USASpending.gov URL from backend data
  const getAwardUrl = (contract) => {
    // Use the URL provided by backend if available
    if (contract.usaspending_url) {
      return contract.usaspending_url;
    }
    // Fallback: if no URL provided but has award_id, return search URL
    if (contract.award_id) {
      return `https://www.usaspending.gov/search/?hash=${contract.award_id}`;
    }
    return null;
  };

  // Use pattern_contracts if available, otherwise fall back to all contracts
  const displayContracts = vendor.pattern_contracts || vendor.contracts || [];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString.replace('Z', '+00:00'));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCompetitionColor = (competition) => {
    if (!competition) return 'default';
    if (competition.includes('NOT COMPETED')) return 'error';
    if (competition.includes('FULL AND OPEN')) return 'success';
    return 'warning';
  };

  const exportToCSV = () => {
    const headers = ['Award ID', 'Award URL', 'Date', 'Amount', 'Agency', 'Office', 'Description', 'NAICS', 'Location', 'Competition', 'Offers'];
    const rows = displayContracts.map(contract => [
      contract.award_id,
      getAwardUrl(contract) || '',
      formatDate(contract.date),
      contract.amount,
      contract.agency,
      contract.office || '',
      contract.award_description || contract.description || '',
      contract.naics_code || '',
      contract.performance_location || '',
      contract.competition || '',
      contract.offers_received || '0'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${vendor.vendor_name.replace(/[^a-z0-9]/gi, '_')}_contracts.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get pattern-specific risk data
  const getRiskPatterns = () => {
    if (patternType === 'split-purchase') {
      return vendor.split_purchase_risk?.patterns || [];
    } else if (patternType === 'repeat-awards') {
      return vendor.repeat_award_risk?.patterns || [];
    }
    return [];
  };

  const riskPatterns = getRiskPatterns();
  const paginatedContracts = displayContracts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">{vendor.vendor_name}</Typography>
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Showing {displayContracts.length} pattern contracts (of {vendor.contract_count} total) • {formatCurrency(vendor.total_amount)}
              </Typography>
              {vendor.vendor_location && (
                <Typography variant="caption" color="text.secondary">
                  • {vendor.vendor_location.city}, {vendor.vendor_location.state}
                </Typography>
              )}
              {vendor.vendor_uei && (
                <Typography variant="caption" color="text.secondary">
                  • UEI: {vendor.vendor_uei}
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <IconButton onClick={exportToCSV} color="primary">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Pattern Context Alert */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {patternType === 'split-purchase' && (
              <>Showing contracts that appear to be split to avoid procurement thresholds. 
              These contracts are within ±3-5% of threshold values ($250K SAT, $10K MPT, etc.) or grouped within short time windows.</>  
            )}
            {patternType === 'repeat-awards' && (
              <>Showing contracts that are part of repeat award patterns. 
              These represent recurring awards for similar products/services with limited competition.</>  
            )}
            {patternType === 'vendor-concentration' && (
              <>Showing all contracts contributing to vendor market concentration. 
              This vendor has a dominant position in their market segment.</>  
            )}
          </Typography>
        </Alert>

        {/* Vendor Summary */}
        {vendor.vendor_location && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Vendor Information</Typography>
                {vendor.vendor_location && (
                  <Typography variant="body2">
                    <strong>Location:</strong> {vendor.vendor_location.address && `${vendor.vendor_location.address}, `}
                    {vendor.vendor_location.city}, {vendor.vendor_location.state} {vendor.vendor_location.zip}
                  </Typography>
                )}
                {vendor.vendor_uei && (
                  <Typography variant="body2">
                    <strong>UEI:</strong> {vendor.vendor_uei}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Contract Summary</Typography>
                <Typography variant="body2">
                  <strong>Date Range:</strong> {formatDate(vendor.date_range?.min)} - {formatDate(vendor.date_range?.max)}
                </Typography>
                <Typography variant="body2">
                  <strong>Top Agencies:</strong> {vendor.agencies?.slice(0, 3).join(', ')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Contracts" />
          <Tab label="Risk Analysis" />
          <Tab label="Timeline" />
        </Tabs>

        {/* Contracts Tab */}
        {activeTab === 0 && (
          <Box mt={2}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Award ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Agency/Office</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Competition</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedContracts.map((contract, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: 'inherit'
                      }}
                    >
                      <TableCell>
                        {contract.award_id ? (
                          <a 
                            href={getAwardUrl(contract)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#1976d2',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <Typography variant="caption" sx={{ 
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}>
                              {contract.award_id}
                            </Typography>
                          </a>
                        ) : (
                          <Typography variant="caption">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(contract.date)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(contract.amount)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {contract.agency}
                        </Typography>
                        {contract.office && (
                          <Typography variant="caption" color="text.secondary">
                            {contract.office}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 200 }} title={contract.award_description || contract.description || 'N/A'}>
                          {contract.award_description || contract.description || 'N/A'}
                        </Typography>
                        {contract.naics_description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            NAICS: {contract.naics_code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {contract.performance_location || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contract.competition || 'Unknown'}
                          size="small"
                          color={getCompetitionColor(contract.competition)}
                          title={`${contract.offers_received || 0} offers received`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={displayContracts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}

        {/* Risk Analysis Tab */}
        {activeTab === 1 && (
          <Box mt={2}>
            {patternType === 'split-purchase' && vendor.split_purchase_risk && (
              <Box>
                <Alert severity={vendor.split_purchase_risk.risk_level === 'CRITICAL' ? 'error' : 'warning'}>
                  <Typography variant="subtitle2">
                    Risk Level: {vendor.split_purchase_risk.risk_level}
                  </Typography>
                  <Typography variant="body2">
                    {vendor.split_purchase_risk.patterns.length} suspicious patterns detected
                  </Typography>
                </Alert>

                {vendor.split_purchase_risk.patterns.map((pattern, index) => (
                  <Paper key={index} sx={{ p: 2, mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Pattern #{index + 1}: Potential Threshold Avoidance
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Threshold Target
                        </Typography>
                        <Typography variant="body1">
                          {pattern.nearest_threshold?.name} ({formatCurrency(pattern.nearest_threshold?.value)})
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Actual Total
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(pattern.total_amount)}
                        </Typography>
                        <Typography variant="caption" color={
                          pattern.nearest_threshold?.position === 'under' ? 'warning.main' : 'error.main'
                        }>
                          {pattern.nearest_threshold?.position === 'under' 
                            ? `${(100 - pattern.nearest_threshold?.proximity * 100).toFixed(1)}% below threshold`
                            : `${((pattern.nearest_threshold?.proximity - 1) * 100).toFixed(1)}% above threshold`
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Contracts in Pattern ({pattern.contracts.length})
                        </Typography>
                        <Box mt={1}>
                          {pattern.contracts.map((contract, idx) => (
                            <Chip
                              key={idx}
                              label={`${formatDate(contract.date)}: ${formatCurrency(contract.amount)}`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}

            {patternType === 'vendor-concentration' && vendor.concentration_risk && (
              <Box>
                <Alert severity={vendor.concentration_risk.risk_level === 'CRITICAL' ? 'error' : 'warning'}>
                  <Typography variant="subtitle2">
                    Market Concentration Risk: {vendor.concentration_risk.risk_level}
                  </Typography>
                </Alert>

                <Grid container spacing={3} mt={1}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {vendor.concentration_risk.market_share_percentage}%
                      </Typography>
                      <Typography variant="caption">Market Share</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {vendor.concentration_risk.contract_share_percentage}%
                      </Typography>
                      <Typography variant="caption">Contract Share</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {vendor.concentration_risk.competitor_count}
                      </Typography>
                      <Typography variant="caption">Competitors</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {vendor.concentration_risk.dominant_naics && (
                  <Paper sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Dominant NAICS Code
                    </Typography>
                    <Typography variant="body1">
                      {vendor.concentration_risk.dominant_naics.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {vendor.concentration_risk.dominant_naics.percentage.toFixed(1)}% of vendor's contracts
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {patternType === 'repeat-awards' && vendor.repeat_award_risk && (
              <Box>
                <Alert severity={vendor.repeat_award_risk.risk_level === 'CRITICAL' ? 'error' : 'warning'}>
                  <Typography variant="subtitle2">
                    Repeat Award Risk: {vendor.repeat_award_risk.risk_level}
                  </Typography>
                  <Typography variant="body2">
                    {vendor.repeat_award_risk.patterns.length} repeat patterns detected
                  </Typography>
                </Alert>

                {vendor.repeat_award_risk.patterns.map((pattern, index) => (
                  <Paper key={index} sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {pattern.product_service}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                          Frequency
                        </Typography>
                        <Typography variant="body1">
                          {pattern.frequency_per_month.toFixed(1)}/month
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                          Pattern
                        </Typography>
                        <Typography variant="body1">
                          {pattern.time_pattern}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                          Avg Competition
                        </Typography>
                        <Typography variant="body1">
                          {pattern.average_competition.toFixed(1)} bidders
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                          Total Value
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(pattern.total_amount)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Timeline Tab */}
        {activeTab === 2 && (
          <Box mt={2} sx={{ overflowY: 'auto', maxHeight: 400 }}>
            <Timeline position="alternate">
              {vendor.contracts
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 20)
                .map((contract, index) => (
                  <TimelineItem key={index}>
                    <TimelineOppositeContent color="text.secondary">
                      {formatDate(contract.date)}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot
                        color={
                          riskPatterns.some(p =>
                            p.contracts?.some(c => c.award_id === contract.award_id)
                          ) ? 'warning' : 'primary'
                        }
                      >
                        {riskPatterns.some(p =>
                          p.contracts?.some(c => c.award_id === contract.award_id)
                        ) ? <WarningIcon /> : <MoneyIcon />}
                      </TimelineDot>
                      {index < vendor.contracts.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="subtitle2">
                        {formatCurrency(contract.amount)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {contract.agency}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {contract.award_description || contract.description}
                      </Typography>
                      {contract.performance_location && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          📍 {contract.performance_location}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
            </Timeline>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorDetailModal;